<template>
  <div class="student-intervention-level-wrapper">
    <section-title
      has-line
      title="Perfil del estudiante en proceso de transición postescolar"
    />
    <div class="row mx-0 mb-4">
      <div class="col-12 col-md-4">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            v-model="value.phase"
            label="¿En qué fase se encuentra el proceso del estudiante?"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="Favor seleccione una fase."
            :options="phases"
          />
        </validation-provider>
      </div>
    </div>
    <section-title has-line title="Documentos" />
    <div class="row mx-0 mb-4 bg-gray p-4 rounded">
      <template v-if="value.phase">
        <div class="col-12 col-md-9">
          <div class="font-14 font-weight-bold  mb-1 pb-1">
            {{ value.phase.name }}
          </div>
        </div>
        <div class="col-12 col-md-3">
          <div class="d-flex align-items-center">
            <validation-provider
              v-slot="{ errors }"
              mode="lazy"
              rules="required"
            >
              <custom-file-input
                ref="fileUpload"
                v-model="value.document"
                :attachment-id="value.documentId"
                display-style="normal"
                :on-download="downloadFile"
                :accept="'.pdf'"
                label-position="left"
                :error="errors[0]"
                custom-error-msg="El documento es requerido."
                :disabled="readonly"
              />
            </validation-provider>
          </div>
        </div>
      </template>
      <div v-else class="col-12 text-center">
        <div class="font-14 pb-1">
          La fase en que se encuentra el estudiante no se ha seleccionado
        </div>
      </div>
    </div>
    <DocumentsForm
      ref="documentsFormRef"
      :documents="additionalDocuments"
      :allowed-document-types="additionalDocumentTypes"
      :multiple-uploads-document-type-ids="[]"
      :has-existing-documents="true"
      :read-only="readonly"
      :show-title="false"
      :minute-document-type-id="documentTypes.SAEE_Minute"
      :is-compu-minute-system-selection-enabled="isCompuMinuteSystemSelectionEnabled"
      :student-sie-id="studentSieId"
      :minute-info="minuteInfo"
      :on-go-to-draft-minute="onGoToDraftMinute"
      @minute-info-input="$emit('minute-info-input', $event)"
    />
    <section-title has-line title="Información Contacto de la Escuela" />
    <div class="row mx-0">
      <div class="col-12 mb-3 col-md-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-input
            v-model="value.schoolContactName"
            label="Nombre del Contacto:"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="El nombre del contacto es requerido."
          />
        </validation-provider>
      </div>
      <div class="col-12 mb-3 col-md-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            v-model="value.schoolContactPosition"
            label="Posición:"
            fieldkey="signaturePositionId"
            fieldname="signaturePositionName"
            placeholder="Seleccione"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="La posición es requerida."
            :options="positions"
          />
        </validation-provider>
      </div>
      <div class="col-12 mb-3 col-md-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-input
            v-model="value.schoolContactPhone"
            label="Teléfono:"
            :mask-type="'Phone'"
            placeholder="xxx-xxx-xxx"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="El teléfono es requerido."
          />
        </validation-provider>
      </div>
      <div class="col-12 mb-3 col-md-3">
        <validation-provider
          v-slot="{ errors }"
          rules="required|email"
          :custom-messages="{
            required: 'El correo electrónico es requerido.',
            email: 'Favor de entrar un correo electrónico válido.'
          }"
        >
          <custom-input
            v-model="value.schoolContactEmail"
            label="Correo Electrónico:"
            type="email"
            placeholder="ejemplo@mail.com"
            :error="errors[0]"
            :disabled="
              readonly ||
                isDirectorOrMaestro ||
                isDirectorPrivadoOrMaestroPrivado
            "
            :custom-error-msg="errors[0]"
          />
        </validation-provider>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from "SectionTitle";
import CustomDropDown from "custom-drop-down";
import CustomInput from "CustomInput";
import CustomFileInput from "CustomFileInput";
import { getStudentReferralPhases } from "@/modules/common/code.api.js";
import DocumentsService from "@/services/documents";
import signatureService from "@/modules/common/signature.api";
import userRole from "@/utils/constants/userRoles";
import DocumentsForm from "@/views/humanResources/request/components/DocumentsForm";
import CompuMinuteSelection from "@/components/shared/CompuMinuteSelection.vue";
import documentTypes from "@/utils/constants/documentTypes";

export default {
  name: "StudentMedicalInfo",
  components: {
    SectionTitle,
    CustomDropDown,
    CustomFileInput,
    CustomInput,
    DocumentsForm,
    CompuMinuteSelection
  },
  props: {
    value: {
      type: Object,
      default: () => {}
    },
    readonly: {
      type: Boolean,
      default: () => false
    },
    additionalDocuments: {
      type: Array,
      default: () => []
    },
    isCompuMinuteSystemSelectionEnabled: {
      type: Boolean,
      default: false
    },
    studentSieId: {
      type: [Number, String],
      default: null
    },
    minuteInfo: {
      type: Object,
      default: () => ({
        useSystemMinute: null,
        compuMinuteId: null,
        fileId: null,
        fileName: null
      })
    },
    onGoToDraftMinute: {
      type: Function,
      default: null
    }
  },
  data: () => ({
    documentTypes,
    additionalDocumentTypes: [
      {
        id: documentTypes.SAEE_Minute,
        name: "Minuta SAEE-08"
      }
    ],
    phases: [],
    positions: [],
    disabled: false
  }),
  computed: {
    hasMinuteAttached() {
      return (
        this.additionalDocuments.some(
          (document) => document.documentTypeId === documentTypes.SAEE_Minute
        ) || this.minuteInfo.useSystemMinute === true
      );
    },
    isDirectorOrMaestro() {
      const roles = [userRole.director, userRole.maestro];
      const isInRole = roles.some(role => role === this.userRole);
      return isInRole;
    },
    isDirectorPrivadoOrMaestroPrivado() {
      const roles = [userRole.directorPrivado, userRole.maestroPrivado];
      const isInRole = roles.some(role => role === this.userRole);
      return isInRole;
    },
  },
  async created() {
    await this.getPhases();
    await this.getPositions();
  },
  methods: {
    async validateMinute() {
      if (!this.isCompuMinuteSystemSelectionEnabled) return true;

      return this.hasMinuteAttached;
    },
    async getPositions() {
      await signatureService.getPositions().then(result => {
        this.positions = result.data;

        this.positions = this.positions.filter(y => y.signatureGroupId == 13);
      });
    },
    async getPhases() {
      const { data } = await getStudentReferralPhases();
      this.phases = [...data];
    },
    downloadFile(attachmentId) {
      return DocumentsService.downloadFromServer(attachmentId);
    }
  },
  mounted() {
    if (this.isDirectorOrMaestro || this.isDirectorPrivadoOrMaestroPrivado) {
      this.value.schoolContactEmail = `d${this.$store.getters["$_user/fullProfile"].sieschoolcode}@de.pr.gov`;
    }
  }
};
</script>
